import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { TrendingUp, Target, Layers, Activity, Flame, ShieldAlert } from "lucide-react";
import api from "../lib/api";
import SignalCard from "./SignalCard";
import { MtClosedTradesModal } from "./MtClosedTradesModal";

const STRATEGY_DESC = {
  "LIQUIDITY POOL": {
    icon: Layers,
    color: "#3B82F6",
    accent: "text-blue-400",
    how: "Individua le zone di liquidità lasciate dal prezzo. Quando il prezzo rompe il massimo (o il minimo) delle ultime 20 candele, viene marcato un \u201cpool di liquidità\u201d sul livello rotto.",
    steps: [
      "Rottura del massimo/minimo delle ultime 20 candele → si crea un pool di liquidità sul livello.",
      "Se il prezzo ritesta il livello entro 10 candele → il pool viene invalidato (movimento troppo debole, nessun segnale).",
      "Se il livello regge almeno 10 candele senza ritest → all'11ª candela si piazza un ordine PENDENTE LIMIT sul livello.",
      "Pool rialzista → BUY LIMIT (il livello diventa supporto). Pool ribassista → SELL LIMIT (resistenza).",
    ],
    idea: "L'obiettivo è entrare sul pullback verso il livello, nella direzione del movimento istituzionale che ha creato la liquidità.",
  },
  "STOCH TREND": {
    icon: Activity,
    color: "#F59E0B",
    accent: "text-amber-400",
    how: "Usa l'oscillatore Stocastico (%K = media a 3 periodi dello Stocastico a 14) come misura della FORZA del trend, non come segnale di inversione.",
    steps: [
      "%K in ipercomprato (≥ 80) per 5 candele consecutive → spinta rialzista forte e persistente.",
      "Alla 6ª candela (Candela F) → BUY \u201ca mercato\u201d (NOW), seguendo il trend.",
      "%K in ipervenduto (≤ 20) per 5 candele consecutive → alla 6ª candela SELL a mercato.",
      "Il setup si riarma dopo 5 candele fuori dalla zona (può ripartire).",
    ],
    idea: "Un oscillatore che resta a lungo in zona estrema segnala momentum, non esaurimento: la strategia cavalca il trend invece di andargli contro.",
  },
  "FAIR LIQUIDITY GAP": {
    icon: Flame,
    color: "#14B8A6",
    accent: "text-teal-400",
    how: "Individua zone di liquidità tramite l'overlap delle ombre delle candele (Fair Liquidity Gap). Ricerca massiva di volumi che assorbono con forza i buyers o i sellers.",
    steps: [
      "Almeno 5 candele consecutive con ombre sovrapposte nella stessa direzione → compare una freccia di ingresso.",
      "Il segnale è confermato solo se allineato al Supertrend: BUY in trend rialzista, SELL in trend ribassista.",
      "Ingresso a mercato (NOW). SL 0,75% dal prezzo, TP pari a 3× lo SL (2,25%).",
      "Al +25% del TP si parzializza (chiude parte) e si porta lo stop a pareggio (BE); il TP resta invariato.",
    ],
    idea: "Sfrutta gli assorbimenti di volume sulle ombre delle candele per entrare nella direzione dei grandi operatori, con rischio contenuto e stop rapido a pareggio.",
  },
  "FLG GROUP": {
    icon: Flame,
    color: "#14B8A6",
    accent: "text-teal-400",
    how: "Dato aggregato delle 3 strategie della famiglia FLG che girano in parallelo: Fair Liquidity Gap, FLG Scalp e FLG Recovery. Le statistiche qui sotto sono la sintesi delle tre.",
    steps: [
      "Fair Liquidity Gap: overlap delle ombre + Supertrend, BE a pareggio dopo il +25% del TP.",
      "FLG Scalp: logica Stocastica (black dots), SL sullo swing, Multi-TP con scale-out di 1/3 a 1:1, 1:2 e 1:3.",
      "FLG Recovery: nasce se lo Scalp colpisce lo SL prima del TP1, stesso TP1 con distanza di SL dimezzata.",
      "Il Win Rate mostrato è la MEDIA ARITMETICA dei win rate delle 3 strategie; i conteggi TP/SL sono la somma.",
    ],
    idea: "Un'unica vista che riassume l'intera famiglia FLG, mantenendo pulita la home pur eseguendo 3 strategie distinte in background.",
  },
};

function Stat({ label, value, sub, color }) {
  return (
    <div className="bg-black/30 border border-white/10 rounded-md p-3">
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className={`font-mono text-xl font-semibold mt-0.5 ${color || "text-white"}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-600 mt-0.5">{sub}</div>}
    </div>
  );
}

const usdAxis = (v, cur = "$") => {
  const a = Math.abs(v);
  if (a >= 1000) return `${cur}${(v / 1000).toLocaleString("it-IT", { maximumFractionDigits: 1 })}k`;
  return `${cur}${v.toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;
};
const dateLbl = (ms) => {
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear()).slice(2)}`;
};

function buildPixels(series, getDate, getVal, tMin, tSpan, useIndex) {
  const n = series.length;
  return series.map((p, i) => {
    const t = useIndex ? tMin + (tSpan * i) / Math.max(1, n - 1) : new Date(getDate(p)).getTime();
    return { t, v: getVal(p) };
  });
}

function seriesDomain(vals) {
  if (!vals.length) return { min: 0, max: 1 };
  let mx = Math.max(...vals), mn = Math.min(...vals);
  const pd = (mx - mn) * 0.08 || Math.abs(mx) * 0.02 || 1;
  return { min: mn - pd, max: mx + pd };
}

// Current trading week only: starts at weekStart with the value reached before it, ends now.
export function weekSeries(points, key, weekStart, zeroBased) {
  if (!weekStart || !points?.length) return points || [];
  const ws = new Date(weekStart).getTime();
  const before = points.filter((p) => new Date(p.date).getTime() < ws);
  const base = before.length ? before[before.length - 1][key] : (zeroBased ? 0 : points[0][key]);
  const week = points.filter((p) => new Date(p.date).getTime() >= ws)
    .map((p) => ({ ...p, [key]: zeroBased ? p[key] - base : p[key] }));
  const start = { date: weekStart, [key]: zeroBased ? 0 : base };
  const last = week.length ? week[week.length - 1][key] : start[key];
  return [start, ...week, { date: new Date().toISOString(), [key]: last }];
}
function maxDrawdown(series, key) {
  let peak = -Infinity, ddAbs = 0, ddPct = 0;
  for (const p of series || []) {
    const v = p[key];
    if (v > peak) peak = v;
    const d = peak - v;
    if (d > ddAbs) { ddAbs = d; ddPct = peak ? (d / peak) * 100 : 0; }
  }
  return { abs: ddAbs, pct: ddPct };
}
export function LineChart({ series, valueKey, color, emptyMsg, cur }) {
  const raw = (series || []).map((p) => ({ t: new Date(p.date).getTime(), v: p[valueKey] }));
  const datesValid = raw.length > 0 && raw.every((p) => !isNaN(p.t));
  const pts = datesValid ? raw : raw.map((p, i) => ({ t: i, v: p.v }));
  if (pts.length < 2) {
    return <div className="text-xs text-zinc-600 text-center py-10">{emptyMsg || "Dati insufficienti per il grafico."}</div>;
  }
  const W = 580, H = 200, padL = 56, padR = 14, padT = 12, padB = 28;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const tMin = Math.min(...pts.map((p) => p.t)), tMax = Math.max(...pts.map((p) => p.t));
  const tSpan = tMax - tMin || 1;
  const d = seriesDomain(pts.map((p) => p.v));
  const xOf = (t) => padL + ((t - tMin) / tSpan) * plotW;
  const yOf = (v) => padT + (1 - (v - d.min) / (d.max - d.min)) * plotH;
  const path = pts.map((p, i) => `${i ? "L" : "M"}${xOf(p.t).toFixed(1)},${yOf(p.v).toFixed(1)}`).join(" ");
  const area = `M${xOf(pts[0].t).toFixed(1)},${yOf(d.min).toFixed(1)} ${pts.map((p) => `L${xOf(p.t).toFixed(1)},${yOf(p.v).toFixed(1)}`).join(" ")} L${xOf(pts[pts.length - 1].t).toFixed(1)},${yOf(d.min).toFixed(1)} Z`;
  const fracs = [0, 0.25, 0.5, 0.75, 1];
  const xc = 5;
  const xTicks = Array.from({ length: xc }, (_, i) => tMin + (tSpan * i) / (xc - 1));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" data-testid="line-chart">
      {fracs.map((f) => (
        <g key={f}>
          <line x1={padL} y1={yOf(d.min + f * (d.max - d.min))} x2={W - padR} y2={yOf(d.min + f * (d.max - d.min))} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
          <text x={padL - 6} y={yOf(d.min + f * (d.max - d.min)) + 3} textAnchor="end" fontSize="9" fill={color} fontFamily="monospace">
            {usdAxis(d.min + f * (d.max - d.min), cur)}
          </text>
        </g>
      ))}
      {datesValid && xTicks.map((t, i) => (
        <text key={`x${i}`} x={xOf(t)} y={H - padB + 15}
          textAnchor={i === 0 ? "start" : i === xc - 1 ? "end" : "middle"}
          fontSize="9" fill="#71717a" fontFamily="monospace">{dateLbl(t)}</text>
      ))}
      <path d={area} fill={`${color}14`} stroke="none" />
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

const RRPill = ({ rr, className }) => (
  <span className={`font-mono ${className}`}>{rr != null ? `1 : ${rr}` : "—"}</span>
);
const fmtPF = (v) => (v == null ? "—" : v >= 999 ? "∞" : v);
const pfColor = (v) => (v == null ? "text-zinc-500" : v >= 1 ? "text-emerald-400" : "text-red-400");

const money = (n, cur = "$") => `${n < 0 ? "-" : ""}${cur}${Math.abs(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function OverallContent({ data }) {
  const t = data.track_record;
  const liq = data.strategies["LIQUIDITY POOL"];
  const stoch = data.strategies["STOCH TREND"];
  const flg = data.strategies["FAIR LIQUIDITY GAP"];
  const profitColor = t.net_profit >= 0 ? "text-emerald-400" : "text-red-400";
  const [real, setReal] = useState(null);
  const [mtStats, setMtStats] = useState(null);
  const [mtOpen, setMtOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/mt/equity-curve")
      .then((r) => { if (alive) setReal(r.data || {}); })
      .catch(() => {});
    api.get("/mt/closed-trades")
      .then((r) => { if (alive) setMtStats(r.data || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const realPoints = real?.points || [];
  const simSeries = t.points;
  const realSeries = realPoints;
  const realWeek = weekSeries(realPoints, "balance", real?.week_start);
  const rInit = real?.initial_balance;
  const rFinal = real?.current_balance;
  const rNet = rInit != null && rFinal != null ? rFinal - rInit : null;
  const rRoi = rInit ? (rNet / rInit) * 100 : null;
  const rProfitColor = rNet != null && rNet >= 0 ? "text-emerald-400" : "text-red-400";
  const rDd = maxDrawdown(realSeries, "balance");

  return (
    <div className="space-y-5" data-testid="insights-overall">
      <div>
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 text-sm font-medium">
            <TrendingUp size={16} className="text-emerald-400" /> Track record
          </div>
        </div>
        <p className="text-xs text-zinc-500">
          Performance basata sui TP/SL realmente colpiti dai segnali (size fissa {t.lot_size} lotti) e andamento reale del conto MT5. Scala in USD.
        </p>
      </div>

      <div className="bg-black/30 border border-emerald-500/20 rounded-md p-3" data-testid="chart-sim">
        <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-400 mb-2">
          <span className="w-3 h-0.5 bg-emerald-400 inline-block" /> Simulazione strategie
        </div>
        <LineChart series={simSeries} valueKey="value" color="#10B981" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="sim-stats">
        <Stat label="Saldo iniziale" value={money(t.starting_balance)} />
        <Stat label="Saldo finale (sim.)" value={money(t.final_balance)} color="text-white" />
        <Stat label="Profitto netto" value={money(t.net_profit)} color={profitColor} />
        <Stat label="ROI" value={`${t.roi_pct}%`} color={profitColor} />
        <Stat label="Operazioni" value={t.total_trades} sub={`${t.wins} TP · ${t.losses} SL`} />
        <Stat label="Win rate" value={`${t.win_rate}%`} color="text-amber-400" sub={t.breakeven ? `${t.breakeven} BE (esclusi)` : undefined} />
        <Stat label="Serie TP max" value={t.max_win_streak} color="text-emerald-400" sub="TP di fila" />
        <Stat label="Serie SL max" value={t.max_loss_streak} color="text-red-400" sub="SL di fila" />
      </div>

      <div className="bg-black/30 border border-blue-500/20 rounded-md p-3" data-testid="chart-real">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-blue-400">
            <span className="w-3 h-0.5 bg-blue-400 inline-block" /> Conto reale MT5
            <span className="text-zinc-500 font-normal" data-testid="chart-real-week">· settimana corrente</span>
          </div>
          <button data-testid="mt-closed-trades-btn" onClick={() => setMtOpen(true)}
            className="text-[11px] px-3 py-1.5 rounded-md bg-blue-600/90 text-white hover:bg-blue-500 transition-colors">
            Trade chiusi MT5
          </button>
        </div>
        <LineChart series={realWeek} valueKey="balance" color="#3B82F6" cur="€"
          emptyMsg="Grafico disponibile dopo almeno 2 giorni di operazioni reali sul conto MT5." />
      </div>

      {mtStats && mtStats.enabled !== false && (mtStats.count > 0 || rInit != null) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="mt-stats">
          <Stat label="Saldo iniziale" value={rInit != null ? money(rInit, "€") : "—"} />
          <Stat label="Saldo finale (reale)" value={rFinal != null ? money(rFinal, "€") : "—"} color="text-white" />
          <Stat label="Profitto netto" value={rNet != null ? money(rNet, "€") : money(mtStats.total_pnl || 0, "€")} color={rProfitColor} />
          <Stat label="ROI" value={rRoi != null ? `${rRoi.toFixed(2)}%` : "—"} color={rProfitColor} />
          <Stat label="Operazioni" value={mtStats.count || 0} sub={`${mtStats.wins || 0} vinti · ${mtStats.losses || 0} persi`} />
          <Stat label="Win rate" value={`${mtStats.win_rate ?? 0}%`} color="text-amber-400" />
          <Stat label="Serie TP max" value={mtStats.max_win_streak || 0} color="text-emerald-400" sub="vinti di fila" />
          <Stat label="Serie SL max" value={mtStats.max_loss_streak || 0} color="text-red-400" sub="persi di fila" />
          <Stat label="Drawdown max" value={rDd.abs > 0 ? `-${money(rDd.abs, "€")}` : "€0,00"} color="text-red-400" sub={rDd.abs > 0 ? `-${rDd.pct.toFixed(2)}%` : "nessuno"} />
        </div>
      )}

      <MtClosedTradesModal open={mtOpen} onClose={() => setMtOpen(false)} />

      <div>
        <div className="flex items-center gap-2 text-sm font-medium mb-2">
          <Target size={16} className="text-blue-400" /> Risk-Reward per strategia
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-black/30 border border-blue-500/20 rounded-md p-3">
            <div className="text-sm text-blue-400 font-medium">Liquidity Pool</div>
            <div className="text-xs text-zinc-400 mt-1">RR medio <RRPill rr={liq.rr} className="text-white" /></div>
            <div className="text-[11px] text-zinc-600 mt-0.5">TP medio +{liq.avg_tp_pct}% · SL medio -{liq.avg_sl_pct}%</div>
          </div>
          <div className="bg-black/30 border border-amber-500/20 rounded-md p-3">
            <div className="text-sm text-amber-400 font-medium">Stoch Trend</div>
            <div className="text-xs text-zinc-400 mt-1">RR medio <RRPill rr={stoch.rr} className="text-white" /></div>
            <div className="text-[11px] text-zinc-600 mt-0.5">TP medio +{stoch.avg_tp_pct}% · SL medio -{stoch.avg_sl_pct}%</div>
          </div>
          {flg ? (
            <div className="bg-black/30 border border-teal-500/20 rounded-md p-3">
              <div className="text-sm text-teal-400 font-medium">Fair Liquidity Gap</div>
              <div className="text-xs text-zinc-400 mt-1">RR medio <RRPill rr={flg.rr} className="text-white" /></div>
              <div className="text-[11px] text-zinc-600 mt-0.5">TP medio +{flg.avg_tp_pct}% · SL medio -{flg.avg_sl_pct}%</div>
            </div>
          ) : null}
        </div>
      </div>
      <p className="text-[11px] text-zinc-600">
        ⓘ I risultati sono una simulazione su conto demo e non costituiscono garanzia di rendimenti futuri.
      </p>
    </div>
  );
}

function StrategySignals({ label, labels, accent }) {
  const [signals, setSignals] = useState(null);
  const wanted = labels || [label];

  useEffect(() => {
    let alive = true;
    api.get("/signals")
      .then((r) => { if (alive) setSignals((r.data || []).filter((s) => wanted.includes(s.strategy_label) && s.status === "active")); })
      .catch(() => { if (alive) setSignals([]); });
    return () => { alive = false; };
  }, [wanted.join(",")]);

  return (
    <div data-testid={`strategy-signals-${label}`}>
      <div className="flex items-center gap-2 text-sm font-medium mb-3">
        <Layers size={16} className={accent} /> Trade attivi della strategia
        {signals && <span className="text-zinc-500 font-normal">({signals.length})</span>}
      </div>
      {signals === null ? (
        <div className="text-sm text-zinc-500 py-4">Caricamento segnali…</div>
      ) : signals.length === 0 ? (
        <div className="text-sm text-zinc-500 py-4 border border-dashed border-white/10 rounded-md text-center">
          Nessun trade attivo per questa strategia.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {signals.map((s) => <SignalCard key={s.id} s={s} hideAuto />)}
        </div>
      )}
    </div>
  );
}

function StrategyContent({ label, strat }) {
  const meta = STRATEGY_DESC[label];
  if (!meta || !strat) return null;
  const isFlgGroup = label === "FLG GROUP";
  const testid = label === "LIQUIDITY POOL" ? "liquidity"
    : label === "FAIR LIQUIDITY GAP" || isFlgGroup ? "flg" : "stoch";
  return (
    <div className="space-y-5" data-testid={`insights-${testid}`}>
      <div>
        <p className="text-sm text-zinc-300 leading-relaxed">{meta.how}</p>
        <ol className="mt-3 space-y-2">
          {meta.steps.map((s, i) => (
            <li key={s} className="flex gap-2 text-sm text-zinc-400">
              <span className={`font-mono text-xs ${meta.accent} shrink-0 mt-0.5`}>{i + 1}.</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
        <p className="mt-3 text-xs text-zinc-500 italic border-l-2 border-white/10 pl-3">{meta.idea}</p>
      </div>

      <StrategySignals
        label={label}
        labels={isFlgGroup ? ["FAIR LIQUIDITY GAP", "FLG SCALP", "FLG RECOVERY"] : undefined}
        accent={meta.accent} />

      <div>
        <div className="flex items-center gap-2 text-sm font-medium mb-2">
          <Target size={16} className={meta.accent} /> Risk-Reward
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Stat label="RR strategia" value={<RRPill rr={strat.rr} className="text-white text-xl" />} />
          <Stat label="TP medio" value={`+${strat.avg_tp_pct}%`} color="text-emerald-400" />
          <Stat label="SL medio" value={`-${strat.avg_sl_pct}%`} color="text-red-400" />
        </div>
        <div className="grid grid-cols-3 gap-3 mt-3">
          <Stat label="Win rate" value={`${strat.win_rate}%`} color="text-amber-400" sub={`${strat.closed} chiusi${strat.be_hit ? ` · ${strat.be_hit} BE` : ""}`} />
          <div className="bg-black/30 border border-white/10 rounded-md p-3 flex items-center gap-2">
            <Flame size={16} className="text-emerald-400" />
            <div><div className="text-[10px] text-zinc-500 uppercase tracking-wider">TP di fila</div>
              <div className="font-mono text-xl font-semibold text-emerald-400">{strat.max_win_streak}</div></div>
          </div>
          <div className="bg-black/30 border border-white/10 rounded-md p-3 flex items-center gap-2">
            <ShieldAlert size={16} className="text-red-400" />
            <div><div className="text-[10px] text-zinc-500 uppercase tracking-wider">SL di fila</div>
              <div className="font-mono text-xl font-semibold text-red-400">{strat.max_loss_streak}</div></div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Stat label="Profit Factor Signal" value={fmtPF(strat.pf_signal)} color={pfColor(strat.pf_signal)} sub="solo TP/SL delle card" />
          <Stat label="Profit Factor Real (PFR)" value={fmtPF(strat.pf_real)} color={pfColor(strat.pf_real)} sub="con BE, estensioni TP e MT5" />
        </div>
      </div>

      {strat.by_tf && strat.by_tf.length > 0 && (
        <div>
          <div className="flex items-center gap-2 text-sm font-medium mb-2">
            <Layers size={16} className={meta.accent} /> Risk-Reward per timeframe
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] text-zinc-500 uppercase tracking-wider border-b border-white/10">
                  <th className="text-left py-2 font-normal">TF</th>
                  <th className="text-right py-2 font-normal">TP%</th>
                  <th className="text-right py-2 font-normal">SL%</th>
                  <th className="text-right py-2 font-normal">RR</th>
                  <th className="text-right py-2 font-normal">Win</th>
                  <th className="text-right py-2 font-normal">PF sig</th>
                  <th className="text-right py-2 font-normal">PFR</th>
                  <th className="text-right py-2 font-normal">Segnali</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {strat.by_tf.map((r) => (
                  <tr key={r.timeframe} className="border-b border-white/5">
                    <td className="py-2 text-white">{r.timeframe}</td>
                    <td className="py-2 text-right text-emerald-400">+{r.avg_tp_pct}</td>
                    <td className="py-2 text-right text-red-400">-{r.avg_sl_pct}</td>
                    <td className="py-2 text-right text-zinc-200">{r.rr != null ? `1:${r.rr}` : "—"}</td>
                    <td className="py-2 text-right text-amber-400">{r.win_rate}%</td>
                    <td className={`py-2 text-right ${pfColor(r.pf_signal)}`}>{fmtPF(r.pf_signal)}</td>
                    <td className={`py-2 text-right ${pfColor(r.pf_real)}`}>{fmtPF(r.pf_real)}</td>
                    <td className="py-2 text-right text-zinc-500">{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {strat.by_tf && strat.by_tf.length > 0 && (
        <div>
          <div className="flex items-center gap-2 text-sm font-medium mb-2">
            <ShieldAlert size={16} className={meta.accent} /> Analisi SL consecutivi
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {strat.by_tf.map((r) => (
              <div
                key={`slseq-${r.timeframe}`}
                data-testid={`sl-streak-tile-${r.timeframe}`}
                className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-4"
              >
                <div className="font-mono text-white text-sm font-semibold mb-3">{r.timeframe}</div>
                <div className="flex justify-between items-center py-1 text-xs">
                  <span className="text-zinc-400">Max SL di fila</span>
                  <span className="font-mono text-base font-semibold text-orange-400" data-testid={`max-sl-streak-${r.timeframe}`}>
                    {r.max_sl_streak ?? 0}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 text-xs">
                  <span className="text-zinc-400">Media TP dopo 2+ SL</span>
                  <span className="font-mono text-base font-semibold text-amber-400" data-testid={`avg-tp-after-2sl-${r.timeframe}`}>
                    {r.avg_tp_after_2sl ?? 0}
                    <span className="ml-1 text-[10px] text-zinc-500 font-sans">
                      {r.sl_streaks_2plus ? `su ${r.sl_streaks_2plus} streak` : "nessuna streak"}
                    </span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {strat.by_asset && strat.by_asset.length > 0 && (
        <div>
          <div className="flex items-center gap-2 text-sm font-medium mb-2">
            <Activity size={16} className={meta.accent} /> Win rate per asset
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] text-zinc-500 uppercase tracking-wider border-b border-white/10">
                  <th className="text-left py-2 font-normal">Asset</th>
                  <th className="text-right py-2 font-normal">TP</th>
                  <th className="text-right py-2 font-normal">SL</th>
                  <th className="text-right py-2 font-normal">BE</th>
                  <th className="text-right py-2 font-normal">Win</th>
                  <th className="text-right py-2 font-normal">Segnali</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {strat.by_asset.map((r) => (
                  <tr key={r.asset} className="border-b border-white/5">
                    <td className="py-2 text-white">{r.asset}</td>
                    <td className="py-2 text-right text-emerald-400">{r.tp_hit}</td>
                    <td className="py-2 text-right text-red-400">{r.sl_hit}</td>
                    <td className="py-2 text-right text-sky-300">{r.be_hit || 0}</td>
                    <td className="py-2 text-right text-amber-400">{r.win_rate}%</td>
                    <td className="py-2 text-right text-zinc-500">{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const FLG_SUB = [
  { key: "FAIR LIQUIDITY GAP", label: "FLG", accent: "text-teal-400", border: "border-teal-500/20" },
  { key: "FLG SCALP", label: "FLG Scalp", accent: "text-purple-400", border: "border-purple-500/20" },
  { key: "FLG RECOVERY", label: "FLG Recovery", accent: "text-zinc-300", border: "border-white/15" },
];

function FlgBreakdown({ strategies }) {
  return (
    <div data-testid="flg-breakdown">
      <div className="flex items-center gap-2 text-sm font-medium mb-2">
        <Layers size={16} className="text-teal-400" /> Dettaglio per singola strategia
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {FLG_SUB.map(({ key, label, accent, border }) => {
          const s = strategies?.[key];
          return (
            <div key={key} data-testid={`flg-sub-${key}`}
              className={`bg-black/30 border ${border} rounded-md p-3 space-y-2`}>
              <div className={`text-sm font-medium ${accent}`}>{label}</div>
              {!s || s.closed === 0 ? (
                <div className="text-[11px] text-zinc-500 py-2">Nessun trade chiuso.</div>
              ) : (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-500">Win rate</span>
                    <span className="font-mono text-amber-400">{s.win_rate}%</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-500">Chiusi</span>
                    <span className="font-mono text-zinc-200">{s.closed}
                      <span className="text-emerald-400 ml-1">{s.tp_hit}TP</span>
                      <span className="text-red-400 ml-1">{s.sl_hit}SL</span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-500">RR</span>
                    <span className="font-mono text-zinc-200">{s.rr != null ? `1:${s.rr}` : "—"}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-500">PF sig / real</span>
                    <span className="font-mono">
                      <span className={pfColor(s.pf_signal)}>{fmtPF(s.pf_signal)}</span>
                      <span className="text-zinc-600 mx-1">/</span>
                      <span className={pfColor(s.pf_real)}>{fmtPF(s.pf_real)}</span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const StrategyInsightsModal = ({ open, onClose, mode, data }) => {
  const titleMap = {
    overall: { t: "Performance complessiva", icon: TrendingUp, color: "text-emerald-400" },
    "LIQUIDITY POOL": { t: "Strategia · Liquidity Pool", icon: Layers, color: "text-blue-400" },
    "STOCH TREND": { t: "Strategia · Stoch Trend", icon: Activity, color: "text-amber-400" },
    "FAIR LIQUIDITY GAP": { t: "Strategia · FLG", icon: Flame, color: "text-teal-400" },
    "FLG GROUP": { t: "Strategia · FLG (media 3 strategie)", icon: Flame, color: "text-teal-400" },
  };
  const head = titleMap[mode] || titleMap.overall;
  const HeadIcon = head.icon;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="strategy-insights-modal"
        className="max-w-2xl max-h-[88vh] overflow-y-auto bg-[#0f0f0f] border-white/10 text-zinc-100">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${head.color}`}>
            <HeadIcon size={18} /> {head.t}
          </DialogTitle>
        </DialogHeader>
        {data && mode ? (
          mode === "overall"
            ? <OverallContent data={data} />
            : mode === "FLG GROUP"
              ? (
                <div className="space-y-5">
                  <FlgBreakdown strategies={data.strategies} />
                  <StrategyContent label={mode} strat={data.strategies[mode]} />
                </div>
              )
              : <StrategyContent label={mode} strat={data.strategies[mode]} />
        ) : (
          <div className="py-12 text-center text-zinc-500 text-sm">Caricamento dati…</div>
        )}
      </DialogContent>
    </Dialog>
  );
};
